import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { listenerMiddleware } from "../../middleware/listenerMiddleware";

const SIDEBAR_STORAGE_KEY = "insaion:sidebarCollapsed";

/**
 * Below this viewport width the sidebar is an overlay rather than a column,
 * so it starts closed and closes again on every navigation. Matches
 * Tailwind's `lg` breakpoint, the point at which the expanded 240px rail
 * stops being affordable next to page content.
 */
export const SIDEBAR_OVERLAY_BREAKPOINT = 1024;

const readPersistedCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
  } catch {
    // Storage disabled (private mode, blocked cookies): fall back to expanded.
    return false;
  }
};

export interface UiState {
  /** Rail is reduced to icons only. Persisted across sessions. */
  sidebarCollapsed: boolean;
  /**
   * Whether the rail is showing as an overlay on narrow viewports. Never
   * persisted — it is derived from the viewport and reset on navigation.
   */
  sidebarMobileOpen: boolean;
}

const initialState: UiState = {
  sidebarCollapsed: readPersistedCollapsed(),
  sidebarMobileOpen: false,
};

export const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    /** Flip the rail between the full and icon-only widths. */
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    /** Explicitly set the rail width. */
    setSidebarCollapsed(state, action: PayloadAction<boolean>) {
      state.sidebarCollapsed = action.payload;
    },
    /** Open/close the narrow-viewport overlay rail. */
    setSidebarMobileOpen(state, action: PayloadAction<boolean>) {
      state.sidebarMobileOpen = action.payload;
    },
  },
});

export const { toggleSidebar, setSidebarCollapsed, setSidebarMobileOpen } =
  uiSlice.actions;

export const uiReducer = uiSlice.reducer;

// Persist the collapsed preference outside the reducer so the reducer itself
// stays pure and testable.
listenerMiddleware.startListening({
  matcher: (action) =>
    toggleSidebar.match(action) || setSidebarCollapsed.match(action),
  effect: (_action, api) => {
    const collapsed = (api.getState() as { uiReducer: UiState }).uiReducer
      .sidebarCollapsed;
    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed));
    } catch {
      // Storage unavailable: the preference simply does not survive reload.
    }
  },
});
